import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  BOTTOM_OFFSET,
  defaultGridOptions,
  GridDimensions,
  MAX_SCROLL_BOTTOM_OFFSET,
} from '../helpers';
import { useGridDimensions } from '../hooks';
import { GameItemPlug } from './GameItemPlug';
import { ShowMoreButton } from './ShowMoreButton';
import { GameV1 } from '@/api/games';
import { GameItem } from '@/components/GameItem';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { RestrictedProviders } from '@/helpers/conf';
import { createGamesListName, getGamesPlugs } from '@/helpers/game';
import { useInfiniteScroll } from '@/hooks/scroll';
import { EmptyGameList } from '@/modules/games/components/EmptyGameList';
import { useCommonStore } from '@/store';

import { ScGamesGrid, ScGamesGridRow } from '../styled';
import { GameItemSize } from '@/components/GameItem/styled';

export interface GamesGridProps extends GridDimensions {
  category?: string;
  provider?: string;

  loadWithScroll?: boolean;
  scrollBottomOffset?: number;
  maxScrollBottomOffset?: number;
  bottomOffset?: number;
  blockScrollHeight?: number;
  plugGray?: boolean;
  isSearchModal?: boolean;
  onGamesLoad?: (games: GameV1[]) => void;
}

const SUB_NAME = 'list';
const defaultGameInfo = {
  games: [],
  pagination: {
    totalCount: 0,
    currentPage: 0,
    nextPage: 0,
    prevPage: 0,
    totalPages: 0,
  },
};

export const GamesGrid: FC<GamesGridProps> = ({
  category,
  provider,

  perChunkObj,
  gridOptions = defaultGridOptions,
  gameSizesConfig, // size of game card

  loadWithScroll = false,
  scrollBottomOffset,
  maxScrollBottomOffset = MAX_SCROLL_BOTTOM_OFFSET,
  bottomOffset = BOTTOM_OFFSET,
  blockScrollHeight,
  plugGray = true,
  isSearchModal,
  onGamesLoad,
}) => {
  const [currentChunk, setCurrentChunk] = useState(1);
  const [gamePlugs, setGamePlugs] = useState<GameV1[]>([]);

  const { perChunk, gridColumns, gameSize } = useGridDimensions({
    perChunkObj,
    gridOptions,
    gameSizesConfig,
  });

  const {
    gamesByCategories,
    gamesByProviders,
    pendingGamesByProviders,
    pendingGamesByCategories,
    categoriesPending,
    getGamesByCategory,
    getGamesByProvider,
    gameCategoriesList,
  } = useCommonStore(
    useShallow((state) => ({
      gamesByCategories: state.gamesByCategories,
      gamesByProviders: state.gamesByProviders,
      pendingGamesByProviders: state.pendingGamesByProviders,
      pendingGamesByCategories: state.pendingGamesByCategories,
      categoriesPending: state.categoriesPending,
      getGamesByCategory: state.getGamesByCategory,
      getGamesByProvider: state.getGamesByProvider,
      gameCategoriesList: state.gameCategoriesList,
    }))
  );

  const fetchGames = useCallback(
    (page: number = 1) => {
      const requestParams = {
        page,
        pageSize: perChunk,
        subName: SUB_NAME,
      };

      if (category) {
        getGamesByCategory(category, requestParams);
      }

      if (provider) {
        getGamesByProvider(provider, {
          ...requestParams,
        });
      }
    },
    [perChunk, category, provider, getGamesByCategory, getGamesByProvider]
  );

  const gamesListName = useMemo(() => {
    const activeParam = category || provider || '';
    return createGamesListName(activeParam, SUB_NAME);
  }, [category, provider]);

  const isLoadingGames = useMemo(
    () =>
      category
        ? pendingGamesByCategories[gamesListName]
        : provider
          ? pendingGamesByProviders[gamesListName]
          : false,
    [
      gamesListName,
      pendingGamesByCategories,
      pendingGamesByProviders,
      category,
      provider,
    ]
  );

  const { games: gamesChunk, pagination } = useMemo(
    () =>
      category
        ? gamesByCategories[gamesListName] || defaultGameInfo
        : provider
          ? gamesByProviders[gamesListName] || defaultGameInfo
          : defaultGameInfo,
    [gamesListName, category, provider, gamesByCategories, gamesByProviders]
  );

  useEffect(() => {
    if (!gameCategoriesList || pagination.currentPage > 1) return;
    if (
      category &&
      (gamesByCategories[`${category}-list`] ||
        pendingGamesByCategories[`${category}-list`])
    )
      return;
    if (
      provider &&
      (gamesByProviders[`${provider}-list`] ||
        pendingGamesByProviders[`${provider}-list`])
    )
      return;

    setCurrentChunk(1);
    fetchGames();
  }, [
    gamesByCategories,
    pendingGamesByCategories,
    gamesByProviders,
    pendingGamesByProviders,
    category,
    provider,
    gameCategoriesList,
    pagination.currentPage,
    setCurrentChunk,
    fetchGames,
  ]);

  useEffect(() => {
    if (!onGamesLoad) return;

    const categoryGames = gamesByCategories[gamesListName]?.games;
    const providerGames = gamesByProviders[gamesListName]?.games;

    if (categoryGames?.length) {
      return onGamesLoad(categoryGames);
    }

    if (providerGames?.length) {
      return onGamesLoad(providerGames);
    }
  }, [
    category,
    gamesByCategories,
    gamesByProviders,
    gamesListName,
    onGamesLoad,
    provider,
  ]);

  const [atTheBottom, continueScroll] = useInfiniteScroll({
    bottomOffset,
    blockScrollHeight,
  });

  const gridArea = useMemo(
    () => currentChunk * perChunk,
    [currentChunk, perChunk]
  );

  const gamesToShow = useMemo(() => {
    return gridArea < gamesChunk.length
      ? gamesChunk.slice(0, gridArea)
      : gamesChunk;
  }, [gamesChunk, gridArea]);

  const nextChunk = useCallback(() => {
    setCurrentChunk((s) => s + 1);
    pagination.nextPage !== null && fetchGames(currentChunk + 1);
  }, [pagination.nextPage, fetchGames, currentChunk]);

  // for modal window
  useEffect(() => {
    if (
      loadWithScroll &&
      scrollBottomOffset != null &&
      scrollBottomOffset < maxScrollBottomOffset
    ) {
      nextChunk();
    }
  }, [nextChunk, loadWithScroll, scrollBottomOffset, maxScrollBottomOffset]);

  useEffect(() => {
    if (loadWithScroll && atTheBottom && gamesChunk.length % perChunk === 0) {
      nextChunk();
      continueScroll();
    }
  }, [
    nextChunk,
    loadWithScroll,
    atTheBottom,
    gamesChunk,
    perChunk,
    continueScroll,
  ]);

  const isShowMoreMode = useMemo(() => {
    return !loadWithScroll && pagination.totalCount - gamesToShow.length !== 0;
  }, [loadWithScroll, pagination.totalCount, gamesToShow.length]);

  useEffect(() => {
    if (perChunk > gamesToShow.length && gamesToShow.length < gridArea) {
      const plugs = getGamesPlugs(gamesChunk, gridColumns, 1);

      setGamePlugs(plugs);
    } else {
      setGamePlugs([]);
    }
  }, [
    perChunk,
    gamesChunk,
    gridArea,
    gamesChunk.length,
    gridColumns,
    gamesToShow.length,
  ]);

  const gamesList = useMemo(() => {
    const isShowMore = isShowMoreMode && pagination.totalPages !== currentChunk;
    return [
      ...(isShowMore ? gamesToShow.slice(0, -1) : gamesToShow),
      ...(isShowMore ? [] : gamePlugs),
    ];
  }, [
    isShowMoreMode,
    pagination.totalPages,
    currentChunk,
    gamesToShow,
    gamePlugs,
  ]);

  return (
    <ScGamesGrid>
      {((isLoadingGames || categoriesPending) && !gamesList.length) ||
      !gamesList.length ? (
        <GamesSkeleton isSearchModal={isSearchModal} />
      ) : provider === RestrictedProviders.AUTOPAY ? (
        <EmptyGameList categoryId={category} />
      ) : (
        <ScGamesGridRow {...gridOptions}>
          {gamesList.map((game) => (
            <Fragment key={`${category || provider}-${game?.id}`}>
              {game?.id ? (
                <GameItem {...game} gameSizesConfig={gameSizesConfig} />
              ) : (
                <GameItemPlug plugGray={plugGray} />
              )}
            </Fragment>
          ))}
          {gamesChunk && pagination.totalCount > perChunk && (
            <ShowMoreButton
              isShowMoreMode={isShowMoreMode}
              onClick={isShowMoreMode ? nextChunk : () => setCurrentChunk(1)}
              darkMode={plugGray}
              smallShowBtn={gameSize === GameItemSize.XXS}
            />
          )}
        </ScGamesGridRow>
      )}
    </ScGamesGrid>
  );
};
